import React, { useEffect, useState } from "react";
import { Vote, Check, BarChart3, Lock } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import SEO from "../components/SEO";

const PollCard = ({ poll, onVoted }) => {
    const [optionId, setOptionId] = useState("");
    const [form, setForm] = useState({ voter_name: "", voter_email: "", voter_phone: "" });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(poll.has_voted || false);
    const [resultsPoll, setResultsPoll] = useState(poll);

    const total = resultsPoll.total_votes ?? 0;
    const showCounts = resultsPoll.options.some((o) => typeof o.votes === "number");

    const submit = async (e) => {
        e.preventDefault();
        if (!optionId) { toast.error("Pick an option"); return; }
        if (!form.voter_name.trim() || !form.voter_email.trim()) {
            toast.error("Name and email are required");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post(`/polls/${poll.id}/vote`, {
                option_id: optionId,
                voter_name: form.voter_name.trim(),
                voter_email: form.voter_email.trim(),
                voter_phone: form.voter_phone.trim() || null,
            });
            setResultsPoll(data);
            setDone(true);
            toast.success("Thanks — your vote was counted.");
            onVoted?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not submit vote");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <article data-testid={`poll-card-${poll.id}`} className="bg-white border border-black/10 rounded-2xl p-6 sm:p-8">
            <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#F2D8D5] flex items-center justify-center shrink-0">
                    <Vote className="w-5 h-5 text-[#E53935]" strokeWidth={1.5} />
                </div>
                <div className="min-w-0">
                    <h2 className="font-heading text-2xl text-[#1C252E] tracking-tight leading-tight">{poll.question}</h2>
                    {poll.description && <p className="text-sm text-[#4A5568] mt-1">{poll.description}</p>}
                </div>
            </div>

            {done || !poll.is_open ? (
                <>
                    {done && (
                        <p className="inline-flex items-center gap-1 text-xs uppercase tracking-wider font-bold text-green-700 bg-green-50 px-3 py-1 rounded-full mb-4" data-testid={`poll-thanks-${poll.id}`}>
                            <Check className="w-3.5 h-3.5" strokeWidth={2} /> Thanks for voting
                        </p>
                    )}
                    {showCounts ? (
                        <ul className="space-y-2.5" data-testid={`poll-results-${poll.id}`}>
                            {resultsPoll.options.map((o) => {
                                const pct = total ? Math.round(((o.votes || 0) / total) * 100) : 0;
                                return (
                                    <li key={o.id}>
                                        <div className="flex justify-between text-sm mb-1">
                                            <span className="text-[#1C252E]">{o.label}</span>
                                            <span className="text-[#4A5568]">{o.votes || 0} · {pct}%</span>
                                        </div>
                                        <div className="h-2 bg-[#F4F1EA] rounded-full overflow-hidden">
                                            <div className="h-full bg-[#E53935] transition-all" style={{ width: `${pct}%` }} />
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    ) : (
                        <p className="text-sm text-[#4A5568] italic flex items-center gap-2"><Lock className="w-3.5 h-3.5" strokeWidth={2} /> Results will be published when the poll closes.</p>
                    )}
                    <p className="text-[10px] uppercase tracking-wider font-bold text-[#4A5568] mt-4">
                        <BarChart3 className="inline w-3 h-3 -mt-0.5" /> {total} total votes
                    </p>
                </>
            ) : (
                <form onSubmit={submit} className="space-y-4" data-testid={`poll-form-${poll.id}`}>
                    <fieldset className="space-y-2" aria-label="Choose an option">
                        {poll.options.map((o) => (
                            <label key={o.id} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition ${optionId === o.id ? "bg-[#F2D8D5]/50 border-[#E53935]" : "bg-white border-black/10 hover:border-[#E53935]/40"}`}>
                                <input
                                    type="radio"
                                    name={`poll-${poll.id}`}
                                    value={o.id}
                                    checked={optionId === o.id}
                                    onChange={() => setOptionId(o.id)}
                                    data-testid={`poll-${poll.id}-option-${o.id}`}
                                    className="accent-[#E53935]"
                                />
                                <span className="text-[#1C252E]">{o.label}</span>
                            </label>
                        ))}
                    </fieldset>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <input
                            data-testid={`poll-${poll.id}-name`}
                            value={form.voter_name}
                            onChange={(e) => setForm({ ...form, voter_name: e.target.value })}
                            placeholder="Your name *"
                            required
                            className="rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935]"
                        />
                        <input
                            data-testid={`poll-${poll.id}-email`}
                            value={form.voter_email}
                            onChange={(e) => setForm({ ...form, voter_email: e.target.value })}
                            type="email"
                            placeholder="Your email *"
                            required
                            className="rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935]"
                        />
                        <input
                            data-testid={`poll-${poll.id}-phone`}
                            value={form.voter_phone}
                            onChange={(e) => setForm({ ...form, voter_phone: e.target.value })}
                            placeholder="Phone (optional)"
                            className="rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935]"
                        />
                    </div>
                    <p className="text-xs text-[#4A5568] flex items-start gap-1.5"><Lock className="w-3 h-3 mt-0.5 shrink-0" strokeWidth={2} /> Your name, email and phone are kept private — only administrators see who voted. Public results show counts only.</p>
                    <button
                        type="submit"
                        disabled={submitting}
                        data-testid={`poll-${poll.id}-submit`}
                        className="inline-flex items-center gap-2 bg-[#E53935] hover:bg-[#D32F2F] disabled:opacity-60 text-white rounded-full px-6 py-2.5 text-sm font-medium transition"
                    >
                        {submitting ? "Submitting…" : "Submit vote"}
                    </button>
                </form>
            )}
        </article>
    );
};

const Polls = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = React.useCallback(async () => {
        try {
            const { data } = await api.get("/polls");
            setRows(data || []);
        } catch (e) {
            toast.error("Could not load polls");
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    return (
        <div className="min-h-screen bg-[#FDFBF7]">
            <SEO title="Community polls" description="Have your say on community decisions. Quick polls — your vote is anonymous to the public." path="/polls" />
            <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 py-16">
                <header className="mb-10">
                    <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#E53935] mb-3">Community voice</p>
                    <h1 className="font-heading text-4xl sm:text-5xl text-[#1C252E] font-medium tracking-tight leading-[1.05]">Polls open right now</h1>
                    <p className="text-base text-[#4A5568] mt-4 max-w-xl">Quick questions from the foundation board. One vote per person — your contact info stays private.</p>
                </header>

                {loading ? (
                    <p className="text-sm text-[#4A5568]">Loading…</p>
                ) : rows.length === 0 ? (
                    <div className="bg-white border border-black/10 rounded-2xl p-10 text-center" data-testid="polls-empty">
                        <Vote className="w-10 h-10 text-[#4A5568] mx-auto mb-3" strokeWidth={1.5} />
                        <p className="font-heading text-xl text-[#1C252E]">No polls right now</p>
                        <p className="text-sm text-[#4A5568] mt-2">Check back soon — we run quick polls before any major board decision.</p>
                    </div>
                ) : (
                    <div className="space-y-6" data-testid="polls-list">
                        {rows.map((p) => <PollCard key={p.id} poll={p} onVoted={load} />)}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Polls;
